/**
 * Star Ratings Component
 *
 * Handles the star ratings functionality.
 */
export class StarRatings {
	/**
	 * Constructor for the StarRatings class.
	 *
	 * @param {HTMLElement} element - The DOM element for the star ratings.
	 */
	constructor( element ) {
		this.ATTR_FULL_STARS = 'data-full-stars';
		this.ATTR_HAS_HALF_STAR = 'data-has-half-star';

		/** @type {HTMLElement} */
		this.element = element;

		/** @type {boolean} */
		this.isInteractive = this.element.hasAttribute( 'data-is-interactive' );
		/** @type {boolean} */
		this.allowDecimals = this.element.hasAttribute( 'data-allow-decimals' );

		/** @type {number} */
		this.fullStarsCount = parseInt( this.element.getAttribute( this.ATTR_FULL_STARS ), 10 ) || 0;

		/** @type {boolean} */
		this.hasHalfStar = this.element.getAttribute( this.ATTR_HAS_HALF_STAR ) === '1';

		/** @type {number} */
		this.maxStars = parseInt( this.element.getAttribute( 'data-max-stars' ), 10 ) || 5;

		/** @type {number} */
		this.value = parseFloat( this.element.getAttribute( 'data-value' ) ) || 0;

		/** @type {number|null} */
		this.tempRating = null;

		/** @type {HTMLElement[]} */
		this.starsElements = this.element.querySelectorAll( '.js-qala-ratings__star' );

		/** @type {HTMLInputElement} */
		this.shownValueElement = this.element.querySelector( '.js-qala-ratings__shown-value' );

		/** @type {HTMLInputElement|null} */
		this.valueElement = null;

		if ( this.isInteractive ) {
			this.valueElement = this.element.querySelector( '.js-qala-ratings__input' );
		}
	}

	/**
	 * Initialize the Star Ratings component.
	 *
	 * @return {void}
	 * @public
	 */
	init() {
		if ( ! this.isInteractive ) {
			return;
		}

		this.setupKeyboardAccessibility();

		this.starsElements.forEach( ( starElement, index ) => {
			this.setupStarElement( starElement, index );
		} );

		this.element.addEventListener( 'mouseleave', () => {
			this.resetStars();
		} );
	}

	/**
	 * Setup keyboard accessibility for the star ratings.
	 *
	 * @return {void}
	 */
	setupKeyboardAccessibility() {
		// Make the container focusable
		this.element.setAttribute( 'tabindex', '0' );
		this.element.setAttribute( 'role', 'slider' );
		this.element.setAttribute( 'aria-valuemin', '1' );
		this.element.setAttribute( 'aria-valuemax', this.maxStars.toString() );
		this.element.setAttribute( 'aria-valuenow', this.value.toString() );
		this.element.setAttribute( 'aria-label', 'Star rating' );

		// Add keyboard event listener
		this.element.addEventListener( 'keydown', event => {
			this.handleKeyboardNavigation( event );
		} );
	}

	/**
	 * Handle keyboard navigation for star ratings.
	 *
	 * @param {KeyboardEvent} event - The keyboard event object.
	 * @return {void}
	 */
	handleKeyboardNavigation( event ) {
		const increment = this.allowDecimals ? 0.5 : 1;

		// Get current rating value as a number
		const currentRating = parseFloat( this.value ) || 0;
		let newRating = currentRating;

		switch ( event.key ) {
			case 'ArrowRight':
			case 'ArrowUp':
				event.preventDefault();
				newRating = currentRating + increment;
				break;

			case 'ArrowLeft':
			case 'ArrowDown':
				event.preventDefault();
				newRating = currentRating - increment;
				break;

			default:
				return;
		}

		// Cap the new rating to a single decimal place to avoid floating point issues
		newRating = Math.round( newRating * 10 ) / 10;

		// Cap new rating to minimum of 1 and maximum of maxStars
		newRating = Math.max( 1, Math.min( newRating, this.maxStars ) );

		// Update the rating
		this.tempRating = newRating;
		this.setRating( newRating );

		// Update ARIA attribute
		this.element.setAttribute( 'aria-valuenow', newRating.toString() );
	}

	/**
	 * Setup event listeners for a star element.
	 *
	 * @param {HTMLElement} starElement - The star element to setup.
	 * @param {number}      index       - The index of the star element.
	 * @return {void}
	 */
	setupStarElement( starElement, index ) {
		starElement.addEventListener( 'click', event => {
			this.handleStarInteraction( starElement, index, event );
			this.setRating( this.tempRating );
		} );

		starElement.addEventListener( 'mousemove', event => {
			this.handleStarInteraction( starElement, index, event );
		} );
	}

	/**
	 * Handle star interaction to determine rating based on cursor position.
	 *
	 * @param {HTMLElement} starElement - The star element being interacted with.
	 * @param {number}      index       - The index of the star element (0-based).
	 * @param {MouseEvent}  event       - The mouse event object.
	 * @return {void}
	 */
	handleStarInteraction( starElement, index, event ) {
		if ( ! this.allowDecimals ) {
			const rating = index + 1;
			this.tempRating = rating;
			this.setRatingTemp( rating, false );
			return;
		}

		// Calculate position within the star element
		const rect = starElement.getBoundingClientRect();
		const relativeX = event.clientX - rect.left;
		const isLeftHalf = relativeX < ( rect.width / 2 );

		if ( isLeftHalf && index > 0 ) {
			const rating = index + 0.5;
			this.tempRating = rating;
			this.setRatingTemp( index, true );
		} else {
			const rating = index + 1;
			this.tempRating = rating;
			this.setRatingTemp( rating, false );
		}
	}

	/**
	 * Reset the stars to the current value (used on mouse leave).
	 *
	 * @return {void}
	 */
	resetStars() {
		if ( this.allowDecimals ) {
			this.element.setAttribute( this.ATTR_FULL_STARS, String( this.fullStarsCount ) );
			this.element.setAttribute( this.ATTR_HAS_HALF_STAR, this.hasHalfStar ? '1' : '0' );
		} else {
			this.element.setAttribute( this.ATTR_FULL_STARS, String( this.fullStarsCount ) );
			this.element.setAttribute( this.ATTR_HAS_HALF_STAR, '0' );
		}
	}

	/**
	 * Set the temporary rating for visual representation.
	 *
	 * @param {number}  rating      - The rating value to set (number of full stars for display).
	 * @param {boolean} hasHalfStar - Whether the rating has a half star.
	 * @return {void}
	 */
	setRatingTemp( rating, hasHalfStar ) {
		if ( this.allowDecimals ) {
			this.element.setAttribute( this.ATTR_FULL_STARS, String( rating ) );
			this.element.setAttribute( this.ATTR_HAS_HALF_STAR, hasHalfStar ? '1' : '0' );
		} else {
			this.element.setAttribute( this.ATTR_FULL_STARS, String( Math.round( rating ) ) );
			this.element.setAttribute( this.ATTR_HAS_HALF_STAR, '0' );
		}
	}

	/**
	 * Set the rating value and update the display.
	 *
	 * @param {number} rating - The rating value to set (as a number).
	 * @return {void}
	 */
	setRating( rating ) {
		// Store the value as a number for calculations
		this.value = rating;
		this.fullStarsCount = Math.floor( rating );

		// Check if there's a decimal part to determine if we have a half star
		const decimalPart = rating - this.fullStarsCount;
		this.hasHalfStar = decimalPart >= 0.5;

		if ( this.valueElement ) {
			this.valueElement.value = String( rating );
		}

		if ( this.allowDecimals ) {
			this.element.setAttribute( this.ATTR_FULL_STARS, String( this.fullStarsCount ) );
			this.element.setAttribute( this.ATTR_HAS_HALF_STAR, this.hasHalfStar ? '1' : '0' );
		} else {
			this.element.setAttribute( this.ATTR_FULL_STARS, String( this.fullStarsCount ) );
			this.element.setAttribute( this.ATTR_HAS_HALF_STAR, '0' );
		}

		// Update shown value if applicable
		if ( this.shownValueElement ) {
			this.shownValueElement.textContent = rating.toString();
		}
	}
}
